import { FC, useCallback, useMemo, useState } from 'react';

import Accordion from '@components/uiKit/Accordion';
import { LoadingSpinner } from '@components/uiKit/LoadingSpinner';

import { scale } from '@scripts/gds';

import { isBooleanFilter, isBrandFilter } from '../../../../utils';
import ActiveFiltersSection from '../ActiveFiltersSection';
import FiltersFooter from '../FiltersFooter';
import IsPreOrderSwitcher from '../IsPreOrderSwitcher';
import PriceFilterSection from '../PriceFilterSection';
import BrandFiltersSection from './components/BrandFiltersSection';
import DesktopFilterItem from './components/DesktopFilterItem';
import {
    getFilterItemContainerStyles,
    leftColumnStyles,
    loaderOverlayStyles,
    mainGridStyles,
    popupBodyStyles,
    rightColumnStyles,
} from './styles';
import { DesktopFiltersPopupContentProps } from './types';

const DesktopFiltersPopupContent: FC<DesktopFiltersPopupContentProps> = ({
    isLoading,
    isBrandPage = false,
    filterData,
    fieldPrefix,
    currentPriceRange,
    currentRangeDefault,
    setRememberedPrice,
    onClose,
    currentTotalCount,
    hidePriceFilter,
    rememberedPrice,
    priceFromUrl,
    priceRange,
    rangeDefault,
    currentFilterData,
    formKey,
    onApplyClick,
    onFilterChange,
}) => {
    const [expandedFilters, setExpandedFilters] = useState<{ [key: string]: boolean }>({});

    const toggleFilter = useCallback((filterCode: string) => {
        setExpandedFilters(prev => ({
            ...prev,
            [filterCode]: !prev[filterCode],
        }));
    }, []);

    const allAccordionUuids = useMemo(() => {
        return currentFilterData
            .filter(filterField => {
                if (isBrandFilter(filterField)) return false;
                const filterItems = filterField.values || [];

                return !isBooleanFilter(filterItems);
            })
            .map(filterField => `popup-${filterField.code}-${formKey}`);
    }, [currentFilterData, formKey]);

    return (
        <>
            <div css={popupBodyStyles}>
                {isLoading && (
                    <div css={loaderOverlayStyles}>
                        <LoadingSpinner embedded noPadding />
                    </div>
                )}
                <ActiveFiltersSection
                    filterData={filterData}
                    fieldPrefix={fieldPrefix}
                    currentPriceRange={currentPriceRange}
                    currentRangeDefault={currentRangeDefault}
                    rememberedPrice={rememberedPrice}
                    setRememberedPrice={setRememberedPrice}
                    onFilterChange={onFilterChange}
                    containerCss={{ padding: '0 30px 16px' }}
                />
                <div css={mainGridStyles}>
                    <div css={leftColumnStyles}>
                        <PriceFilterSection
                            hidePriceFilter={hidePriceFilter}
                            fieldPrefix={fieldPrefix}
                            currentPriceRange={currentPriceRange}
                            priceRange={priceRange}
                            rangeDefault={rangeDefault}
                            rememberedPrice={rememberedPrice}
                            priceFromUrl={priceFromUrl}
                            setRememberedPrice={setRememberedPrice}
                            onFilterChange={onFilterChange}
                        />

                        {!isBrandPage && (
                            <BrandFiltersSection
                                currentFilterData={currentFilterData}
                                fieldPrefix={fieldPrefix}
                                formKey={formKey}
                                onFilterChange={onFilterChange}
                            />
                        )}

                        <div
                            css={{
                                marginTop: scale(3),
                            }}
                        >
                            <IsPreOrderSwitcher fieldPrefix={fieldPrefix} onFilterChange={onFilterChange} />
                        </div>
                    </div>

                    <div css={rightColumnStyles}>
                        <Accordion key={formKey} animationType="height" preExpanded={allAccordionUuids}>
                            {currentFilterData
                                .filter(filterField => !isBrandFilter(filterField))
                                .map((filterField, index) => (
                                    <div key={filterField.code} css={getFilterItemContainerStyles(index)}>
                                        <DesktopFilterItem
                                            filterField={filterField}
                                            fieldPrefix={fieldPrefix}
                                            formKey={formKey}
                                            expandedFilters={expandedFilters}
                                            onToggle={toggleFilter}
                                            onFilterChange={(fieldName, newValue) => {
                                                onFilterChange?.(fieldName, newValue);
                                            }}
                                        />
                                    </div>
                                ))}
                        </Accordion>
                    </div>
                </div>
            </div>
            <FiltersFooter
                isMobile={false}
                onClose={onClose}
                currentTotalCount={currentTotalCount}
                onApplyClick={onApplyClick}
            />
        </>
    );
};

export default DesktopFiltersPopupContent;
